import { Link } from "@tanstack/react-router";
import { cn, cva } from "lib/cn";
import { ComponentProps, FC } from "react";

type LinkSearch = ComponentProps<typeof Link>["search"];

export interface NavigationLink {
    to: ComponentProps<typeof Link>["to"];
    search?: Record<string, unknown>;
    name: string;
    Icon?: FC<{ className?: string }>;
    links?: NavigationLink[];
    isSub?: boolean;
}

const link = cva(
    `flex flex-row items-center rounded-lg hover:bg-brand-shine focus:outline-hidden focus:ring-0`,
    {
        variants: {
            variant: {
                sub: "text-sm h-9",
                admin: "text-sm h-9",
                default: "h-10",
            },
            collapsed: {
                true: "px-0 justify-center",
                false: "px-1",
            },
        },
        defaultVariants: {
            variant: "default",
            collapsed: false,
        },
    },
);

export const NavigationLinks = ({
    links,
    isSubLinks,
    isCollapsed,
}: {
    links: NavigationLink[];
    isSubLinks?: boolean;
    isCollapsed?: boolean;
}) => {
    return (
        <ul className="___space-y-2 p-0">
            {links.map(({ to, search, name, Icon, links, isSub = false }) => (
                <li
                    key={name}
                    className={cn(
                        "relative",
                        (isSubLinks || isSub) &&
                            (isCollapsed
                                ? "pl-0 before:content-none after:content-none"
                                : "pl-14 before:absolute before:top-1/2 before:left-6 before:h-[2px] before:w-6 before:translate-x-[-4px] before:-translate-y-1/2 before:rounded-r-none before:bg-[#24a2c1] before:shadow-none before:content-[''] after:absolute after:top-0 after:bottom-0 after:left-6 after:w-[2px] after:translate-x-[-4px] after:rounded-l-none after:bg-[#24a2c1] after:content-[''] last:after:h-1/2 last:after:rounded-bl-lg"),
                    )}
                >
                    <Link
                        to={to as "/social"}
                        search={search as LinkSearch}
                        className={link({
                            variant: isSubLinks || isSub ? "sub" : "default",
                            collapsed: isCollapsed,
                        })}
                        activeProps={{
                            className: "bg-brand-shine",
                        }}
                        inactiveProps={{
                            className: "",
                        }}
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                    >
                        {Icon && (
                            <Icon
                                className={`h-[32px] w-[32px] ${isCollapsed ? "mr-0" : "mr-3"}`}
                            />
                        )}
                        {!isCollapsed && <div className="text-normal font-normal">{name}</div>}
                    </Link>
                    {links ? (
                        <NavigationLinks links={links} isSubLinks isCollapsed={isCollapsed} />
                    ) : null}
                </li>
            ))}
        </ul>
    );
};
